<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard class="h-full flex flex-col overflow-y-auto">
            <template #header>
                <h3 class="text-base font-medium leading-6 text-gray-800">
                    {{ template ? `Edit ${template.name}` : 'New Template' }}
                </h3>
            </template>

            <div class="space-y-6">
                <UFormGroup label="Name" required>
                    <UInput v-model="model.name" size="xl" />
                </UFormGroup>

                <UFormGroup label="Description">
                    <UInput v-model="model.description" size="xl" />
                </UFormGroup>

                <UFormGroup label="Channel" required>
                    <USelectMenu
                        v-model="model.channel"
                        :options="channelOptions"
                        option-attribute="label"
                        value-attribute="value"
                        :disabled="!!template"
                        size="xl"
                    />
                </UFormGroup>

                <template v-if="model.channel === 'whatsapp'">
                    <UFormGroup label="Category" required>
                        <USelectMenu
                            v-model="model.category"
                            :options="['marketing', 'utility', 'authentication']"
                            size="xl"
                        />
                    </UFormGroup>
                    <UFormGroup label="Language" required>
                        <USelectMenu v-model="model.language" :options="['en', 'ar']" size="xl" />
                    </UFormGroup>
                </template>

                <UFormGroup label="Content" required>
                    <OverlayTemplateContentEmailContent v-if="model.channel === 'email'" v-model="model.content" />
                    <OverlayTemplateContentSmsContent v-else-if="model.channel === 'sms'" v-model="model.content" />
                    <OverlayTemplateContentWhatsappContent v-else-if="model.channel === 'whatsapp'" v-model="model.content" />
                    <OverlayTemplateContentPushContent v-else-if="model.channel === 'push'" v-model="model.content" />
                </UFormGroup>

                <UFormGroup v-if="variables.length" label="Variable samples">
                    <p class="text-xs text-gray-500 mb-2">
                        Meta needs a realistic example for each variable to review the template.
                    </p>
                    <div v-for="(v, i) in variables" :key="v.name" class="flex items-center gap-2 mb-2">
                        <span class="w-40 shrink-0 text-sm font-mono text-gray-600">{{ v.name }}</span>
                        <UInput v-model="variables[i].sample" placeholder="e.g. Rita" class="flex-1" />
                    </div>
                </UFormGroup>
            </div>

            <template #footer>
                <div class="flex justify-end gap-2">
                    <UButton color="gray" variant="ghost" @click="$emit('update:open', false)">Cancel</UButton>
                    <UButton color="black" :loading="saving" @click="save">Save</UButton>
                </div>
            </template>
        </UCard>
    </USlideover>
</template>

<script setup lang="ts">
import type { Template, TemplateVariable } from '~/types/template'
import { syncVariables } from '~/utils/template-variables'
import { templateBodyText } from '~/utils/template'

const props = defineProps<{ open: boolean; template?: Template | null }>()
const emit = defineEmits<{ 'update:open': [boolean]; saved: [Template] }>()

const toast = useToast()
const saving = ref(false)

const channelOptions = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: 'Push', value: 'push' },
]

const model = reactive({
    name: props.template?.name ?? '',
    description: props.template?.description ?? '',
    channel: props.template?.channel ?? 'email',
    category: props.template?.category ?? 'utility',
    language: props.template?.language ?? 'en',
    content: { ...(props.template?.content ?? {}) },
})

const variables = ref<TemplateVariable[]>(props.template?.variables ?? [])

function initialize() {
    model.name = props.template?.name ?? ''
    model.description = props.template?.description ?? ''
    model.channel = props.template?.channel ?? 'email'
    model.category = props.template?.category ?? 'utility'
    model.language = props.template?.language ?? 'en'
    model.content = { ...(props.template?.content ?? {}) }
    variables.value = props.template?.variables ? [...props.template.variables] : []
}

watch(() => props.open, (open) => { if (open) initialize() })

watch(() => model.content, (content) => {
    variables.value = syncVariables(templateBodyText(model.channel, content), variables.value)
}, { deep: true, immediate: true })

async function save() {
    saving.value = true
    try {
        const payload = { ...model, variables: variables.value }
        const saved = props.template
            ? await $fetch<Template>(`/api/templates/${props.template.id}`, { method: 'PATCH', body: payload })
            : await $fetch<Template>('/api/templates', { method: 'POST', body: payload })
        emit('saved', saved)
        emit('update:open', false)
    } catch (e: any) {
        toast.add({ title: 'Save failed', description: e?.data?.message ?? e?.message, color: 'red' })
    } finally {
        saving.value = false
    }
}
</script>
